import { faPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useMemo } from 'react';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import PopUp from 'src/components/design-system/PopUp';

import {
  convertMultiButtonPermission,
  convertSingleButtonPermission,
  EditIconPermissions,
  EditInvoiceAccountingPeriod,
  EditPeriodButtonPermissions,
  EditPeriodsButtonPermissions,
  ViewPeriodPermissions,
} from 'src/sections/view_invoice_budget_period';
import { hasPermission } from 'src/utils/JWT';
import { fToMMDDYY } from 'src/utils/formatTime';
import { getBgColor, getIcon, informationIcon } from 'src/utils/icons';
import { useContextSelector } from 'use-context-selector';
import { getEditPeriodsButtonText, InvoiceContext } from '../InvoiceDetailsContext';
import BudgetPeriodDropdown from './BudgetPeriodDropdown';

interface BudgetPeriodProps {}

const BudgetPeriod: FC<BudgetPeriodProps> = () => {
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);

  const isEditPeriodDisable = useContextSelector(
    InvoiceContext,
    (value) => value.isEditPeriodDisable
  );
  const editPeriodErrorMessage = useContextSelector(
    InvoiceContext,
    (value) => value.editPeriodErrorMessage
  );
  const warnings = useContextSelector(InvoiceContext, (value) => value.warnings);
  const mpaPermissionNumber = useContextSelector(
    InvoiceContext,
    (value) => value.mpaPermissionNumber
  );
  const isInvoiceMPA = useContextSelector(InvoiceContext, (value) => value.isInvoiceMPA);
  const dropdownValues = useContextSelector(InvoiceContext, (value) => value.dropDownValues);
  const handleOpenSidePanel = useContextSelector(
    InvoiceContext,
    (value) => value.handleOpenSidePanel
  );

  const budgetPeriod: string = useMemo(() => {
    if (invoice?.budgetPeriodStartDate && invoice?.budgetPeriodEndDate) {
      return `${fToMMDDYY(invoice?.budgetPeriodStartDate)} - ${fToMMDDYY(
        invoice?.budgetPeriodEndDate
      )}`;
    }
    return '';
  }, [invoice?.budgetPeriodEndDate, invoice?.budgetPeriodStartDate]);

  const titleText: string = useMemo(() => {
    if (
      invoice?.budgetPeriodStartDate &&
      invoice?.budgetPeriodEndDate &&
      dropdownValues.some((value) => value.id === invoice?.glPeriodId)
    ) {
      return `${fToMMDDYY(invoice?.budgetPeriodStartDate)} - ${fToMMDDYY(
        invoice?.budgetPeriodEndDate
      )}`;
    }
    return '';
  }, [
    dropdownValues,
    invoice?.budgetPeriodEndDate,
    invoice?.budgetPeriodStartDate,
    invoice?.glPeriodId,
  ]);

  const isEditIconVisible = hasPermission(EditIconPermissions, mpaPermissionNumber);
  const isViewIconVisible = hasPermission(ViewPeriodPermissions, mpaPermissionNumber);
  const canEditAccountingPeriod = hasPermission(EditInvoiceAccountingPeriod, mpaPermissionNumber);
  const isEditPeriodsTxt = hasPermission(EditPeriodsButtonPermissions, mpaPermissionNumber);
  const isEditPeriodTxt = hasPermission(EditPeriodButtonPermissions, mpaPermissionNumber);
  const isConvertPeriodsTxt = hasPermission(convertMultiButtonPermission, mpaPermissionNumber);
  const isConvertPeriodTxt = hasPermission(convertSingleButtonPermission, mpaPermissionNumber);
  const title = getEditPeriodsButtonText(
    isEditPeriodsTxt,
    isEditPeriodTxt,
    isConvertPeriodsTxt,
    isConvertPeriodTxt
  );

  const hasErrors = !!warnings.accountingPeriod?.length;
  return (
    <div
      title={isInvoiceMPA ? 'Multiple Periods Assigned' : titleText}
      className={`p-1 ${getBgColor([], warnings.accountingPeriod)} text-[13px]`}
    >
      <PopUp
        content={
          <div className="flex flex-col gap-2">
            <PopUpContentWrapper type="Warning" contents={warnings.accountingPeriod} />
          </div>
        }
        show={hasErrors}
        popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
        anchorAlign={{ horizontal: 'right', vertical: 'top' }}
      >
        <div className="flex items-center">
          <p className="font-bold pb-1 flex items-center gap-1 ">
            Accounting Period
            {isEditIconVisible && (
              <PopUp
                show={isEditPeriodDisable}
                content={
                  <div className="m-1 max-w-[300px]">
                    <PopUpContentWrapper type="Error" contents={editPeriodErrorMessage} />
                  </div>
                }
              >
                <button
                  onClick={handleOpenSidePanel}
                  className={`flex justify-center items-center  w-4 h-4 ${
                    isEditPeriodDisable ? 'bg-gray-500 ' : 'bg-info'
                  }  rounded-full cursor-pointer focus:ring-1 focus:ring-black`}
                  title={title}
                  type="submit"
                  disabled={isEditPeriodDisable}
                >
                  <FontAwesomeIcon icon={faPen} color="white" size="xs" />
                </button>
              </PopUp>
            )}
            {isViewIconVisible && (
              <button
                onClick={handleOpenSidePanel}
                className={`flex justify-center items-center w-4 h-4 bg-info rounded-full cursor-pointer focus:ring-1 focus:ring-black`}
                title="View Periods"
                type="submit"
              >
                <div>{informationIcon}</div>
              </button>
            )}
          </p>
          <span className="px-2">{getIcon([], warnings.accountingPeriod)}</span>
        </div>

        {isInvoiceMPA ? (
          <p>Multiple Periods Assigned</p>
        ) : canEditAccountingPeriod ? (
          <BudgetPeriodDropdown />
        ) : (
          <span>{budgetPeriod}</span>
        )}
      </PopUp>
    </div>
  );
};

export default BudgetPeriod;
